import { useMemo, useState } from "react";
import i18n from "../../../language/i18n";
import { Bar as BarRaw, Radar as RadarRaw } from "react-chartjs-2";
import "../../../utilization/ChartSetup";
import categorySkillMappingList from "../../../data/category-skill-mapping-list.json";
import skillVersionCodeMEBList from "../../../data/skill-version-code-meb-list.json";
import categoryVersionCodeMEBList from "../../../data/category-version-code-meb-list.json";
import CheckBoxGlyph from "../CheckBoxGlyph";
import { EmptyState } from "../../../library";
import { skillGraphStyles } from "./SkillGraphStyles";

const Bar = BarRaw as any;
const Radar = RadarRaw as any;

const SkillGraph = (props: any) => {
  const {
    branchAssignmentOutcomeReportList = [],
    personalizedAssignmentOutcomeReportList = [],
    branchAssignmentSkillReportList = [],
    coterieType,
    visibleSourceIds,
  } = props;

  const [showBranchAssignment, setShowBranchAssignment] = useState(true);
  const [showPersonalizedAssignment, setShowPersonalizedAssignment] =
    useState(true);
  // Same two-mode contract the outcome graphs use: a parent that hoists the Kaynak control passes the
  // selection in and the local chips stand down, while a standalone caller keeps its own state. This
  // tab was the last surface on that axis still merging both ödev sources unconditionally — so
  // unticking bireysel ödev on the outcome tab had it merged back one click away.
  const isSourceSelectionControlled = visibleSourceIds !== undefined;
  const sourceIsOn = (sourceId: string, uncontrolledValue: boolean) =>
    isSourceSelectionControlled
      ? (visibleSourceIds || []).includes(sourceId)
      : uncontrolledValue;

  const hasBranchAssignmentData =
    branchAssignmentOutcomeReportList.length > 0;
  const hasPersonalizedAssignmentData =
    personalizedAssignmentOutcomeReportList.length > 0;
  const hasBothSources =
    hasBranchAssignmentData && hasPersonalizedAssignmentData;

  const outcomeReportList = useMemo(() => {
    return [
      ...(sourceIsOn("BRANCH_ASSIGNMENT", showBranchAssignment)
        ? branchAssignmentOutcomeReportList
        : []),
      ...(sourceIsOn("PERSONALIZED_ASSIGNMENT", showPersonalizedAssignment)
        ? personalizedAssignmentOutcomeReportList
        : []),
    ];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    showBranchAssignment,
    showPersonalizedAssignment,
    visibleSourceIds,
    branchAssignmentOutcomeReportList,
    personalizedAssignmentOutcomeReportList,
  ]);

  // Build mapping: category CODE (topic-level like "M.5.1.") → array of skill category IDs
  const codeToSkillIdsMap = useMemo(() => {
    const catById = {};
    categoryVersionCodeMEBList.forEach((c) => {
      catById[c.id] = c;
    });
    const map = {};
    categorySkillMappingList.forEach(({ categoryId, skillCategoryId }) => {
      const cat = catById[categoryId];
      if (!cat) return;
      const code = cat.code;
      if (!map[code]) {
        map[code] = [];
      }
      map[code].push(skillCategoryId);
    });
    return map;
  }, []);

  const mappingCodes = useMemo(
    () => Object.keys(codeToSkillIdsMap),
    [codeToSkillIdsMap],
  );

  // Collect leaf-level category scores from the outcome report tree
  // Returns map: categoryCode → array of activity scores
  const collectLeafCategoryScores = (reportList) => {
    const categoryScoresMap = {};

    const walkTree = (node) => {
      const subs = node.subCategoryActivityAnswerDTOList;
      if (subs && subs.length > 0) {
        subs.forEach(walkTree);
      } else {
        const scores =
          node.activityScoreDTOSet || node.activityScoreList || [];
        const scoresArray = Array.isArray(scores) ? scores : Array.from(scores);
        if (scoresArray.length > 0 && node.categoryDTO) {
          const code = node.categoryDTO.code;
          if (!categoryScoresMap[code]) {
            categoryScoresMap[code] = [];
          }
          categoryScoresMap[code].push(...scoresArray);
        }
      }
    };

    reportList.forEach(walkTree);
    return categoryScoresMap;
  };

  const findRootSkill = (skill) => {
    let current = skill;
    while (current && current.parentCode) {
      const parent = skillVersionCodeMEBList.find(
        (s) => s.code === current.parentCode,
      );
      if (!parent) break;
      current = parent;
    }
    return current;
  };

  // Map specific subject coterieTypes to their parent domain skill coterieType
  const coterieTypeToDomainMap = {
    PHYSICS: "SCIENCE",
    CHEMISTRY: "SCIENCE",
    BIOLOGY: "SCIENCE",
    GEOMETRY: "MATHEMATICS",
    HISTORY: "SOCIAL_STUDIES",
    HISTORY_I: "SOCIAL_STUDIES",
    HISTORY_II: "SOCIAL_STUDIES",
    GEOGRAPHY: "SOCIAL_STUDIES",
    GEOGRAPHY_I: "SOCIAL_STUDIES",
    GEOGRAPHY_II: "SOCIAL_STUDIES",
    REVOLUTION_HISTORY: "SOCIAL_STUDIES",
    PHILOSOPHY: "SOCIAL_STUDIES",
    LITERATURE: "TURKISH",
    ENGLISH: "FOREIGN_LANGUAGE",
    PHILOSOPHY_OR_CULTURE_AND_RELIGION_KNOWLEDGE:
      "CULTURE_AND_RELIGION_KNOWLEDGE",
  };

  const getRelevantRootCodes = () => {
    const domainCoterieType =
      coterieTypeToDomainMap[coterieType] || coterieType;
    const rootSkills = skillVersionCodeMEBList.filter(
      (s) => s.parentCode === null,
    );
    return rootSkills
      .filter(
        (s) => s.coterieType === domainCoterieType || s.coterieType === null,
      )
      .map((s) => s.code);
  };

  // Map leaf outcome codes to skill IDs via code-prefix matching
  // e.g. leaf "M.5.1.1.1." matches mapping topic "M.5.1." because it starts with it
  const computeSkillScores = () => {
    // If the backend supplies explicit per-skill scores from activity.skillIdSet,
    // use them directly instead of deriving via the category→skill mapping.
    // sourceIsOn, not the raw local flag: in controlled mode that flag keeps its initial `true`, so
    // reading it here would let the backend's explicit per-skill scores through even with şube ödevi
    // unticked in the Kaynak row — the same leak this component just closed on the derived path.
    if (
      sourceIsOn("BRANCH_ASSIGNMENT", showBranchAssignment) &&
      branchAssignmentSkillReportList.length > 0
    ) {
      const skillScoresMap: any = {};
      branchAssignmentSkillReportList.forEach((entry: any) => {
        const skillId = entry.skillId;
        if (!skillId) return;
        const scores = entry.activityScoreDTOSet || entry.activityScoreList || [];
        const scoresArray = Array.isArray(scores) ? scores : Array.from(scores);
        if (!skillScoresMap[skillId]) skillScoresMap[skillId] = [];
        skillScoresMap[skillId].push(...scoresArray);
      });
      return skillScoresMap;
    }

    const categoryScoresMap = collectLeafCategoryScores(outcomeReportList);
    const skillScoresMap: any = {};

    for (const [leafCode, scoresValue] of Object.entries(categoryScoresMap)) {
      const scores = scoresValue as any[];
      // Find all mapping codes that are a prefix of this leaf code
      const matchingMappingCodes = mappingCodes.filter((mc) =>
        leafCode.startsWith(mc),
      );
      for (const mc of matchingMappingCodes) {
        const skillIds = codeToSkillIdsMap[mc];
        for (const skillId of skillIds) {
          if (!skillScoresMap[skillId]) {
            skillScoresMap[skillId] = [];
          }
          skillScoresMap[skillId].push(...scores);
        }
      }
    }

    return skillScoresMap;
  };

  const computeAverageScore = (scores) => {
    if (!scores || scores.length === 0) return 0;
    let totalScore = 0;
    scores.forEach((s) => {
      totalScore += s.score;
    });
    return (totalScore / scores.length) * 100;
  };

  const skillScoresMap = computeSkillScores();
  const relevantRootCodes = getRelevantRootCodes();

  const rootSkillData = useMemo(() => {
    const rootSkills = skillVersionCodeMEBList.filter(
      (s) => s.parentCode === null && relevantRootCodes.includes(s.code),
    );

    return rootSkills
      .map((rootSkill) => {
        const descendantIds = skillVersionCodeMEBList
          .filter((s) => {
            if (s.id === rootSkill.id) return true;
            let current = s;
            while (current && current.parentCode) {
              if (current.parentCode === rootSkill.code) return true;
              const parent = skillVersionCodeMEBList.find(
                (p) => p.code === current.parentCode,
              );
              if (!parent) break;
              current = parent;
            }
            return false;
          })
          .map((s) => s.id);

        const allScores = [];
        descendantIds.forEach((id) => {
          if (skillScoresMap[id]) {
            allScores.push(...skillScoresMap[id]);
          }
        });

        const avgScore = computeAverageScore(allScores);

        return {
          code: rootSkill.code,
          name: i18n.t(rootSkill.name),
          score: avgScore,
          hasData: allScores.length > 0,
        };
      })
      .filter((d) => d.hasData);
  }, [skillScoresMap, relevantRootCodes]);

  const leafSkillData = useMemo(() => {
    const parentCodes = new Set(
      skillVersionCodeMEBList
        .filter((s) => s.parentCode)
        .map((s) => s.parentCode),
    );
    const leafSkills = skillVersionCodeMEBList.filter(
      (s) =>
        s.parentCode &&
        !parentCodes.has(s.code) &&
        relevantRootCodes.some((rootCode) => {
          let current = s;
          while (current && current.parentCode) {
            if (current.parentCode === rootCode) return true;
            const parent = skillVersionCodeMEBList.find(
              (p) => p.code === current.parentCode,
            );
            if (!parent) break;
            current = parent;
          }
          return false;
        }),
    );

    return leafSkills
      .map((skill) => {
        const scores = skillScoresMap[skill.id] || [];
        const avgScore = computeAverageScore(scores);
        const rootSkill = findRootSkill(skill);

        return {
          code: skill.code,
          name: i18n.t(skill.name),
          rootCode: rootSkill ? rootSkill.code : "",
          score: avgScore,
          hasData: scores.length > 0,
        };
      })
      .filter((d) => d.hasData)
      .sort((a, b) => a.code.localeCompare(b.code));
  }, [skillScoresMap, relevantRootCodes]);

  // Distinct colors for each root skill (up to 3 visible at a time)
  const rootSkillColors = [
    { bg: "rgba(87, 194, 211, 0.35)", border: "rgba(87, 194, 211, 1)" },   // catchup-blue / teal
    { bg: "rgba(139, 92, 246, 0.35)", border: "rgba(139, 92, 246, 1)" },   // violet
    { bg: "rgba(245, 158, 11, 0.35)", border: "rgba(245, 158, 11, 1)" },   // amber
  ];

  const getRootColor = (rootCode) => {
    const idx = rootSkillData.findIndex((d) => d.code === rootCode);
    return rootSkillColors[idx % rootSkillColors.length];
  };

  const radarData = {
    labels: rootSkillData.map((d) => {
      const words = d.name.split(" ");
      if (words.length <= 2) return d.name;
      const mid = Math.ceil(words.length / 2);
      return [words.slice(0, mid).join(" "), words.slice(mid).join(" ")];
    }),
    datasets: [
      {
        label: i18n.t("learner_score"),
        data: rootSkillData.map((d) => d.score),
        backgroundColor: "rgba(87, 194, 211, 0.1)",
        borderColor: "rgba(87, 194, 211, 0.3)",
        borderWidth: 1,
        pointBackgroundColor: rootSkillData.map(
          (_, i) => rootSkillColors[i % rootSkillColors.length].border,
        ),
        pointBorderColor: "#fff",
        pointRadius: 6,
        pointHoverRadius: 8,
      },
    ],
  };

  const radarOptions = {
    responsive: true,
    maintainAspectRatio: true,
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        backgroundColor: "rgba(17, 72, 89, 0.9)",
        titleFont: { size: 14, family: "Quicksand" },
        bodyFont: { size: 13, family: "Quicksand" },
        padding: 12,
        cornerRadius: 8,
        callbacks: {
          title: (items) => {
            const idx = items[0].dataIndex;
            return rootSkillData[idx].name;
          },
          label: (context) =>
            `${i18n.t("learner_score")}: ${context.parsed.r.toFixed(1)}%`,
        },
      },
    },
    scales: {
      r: {
        beginAtZero: true,
        max: 100,
        ticks: {
          stepSize: 20,
          font: { size: 12, family: "Quicksand" },
          color: "#799BA6",
          backdropColor: "transparent",
          callback: (value) => `${value}%`,
        },
        pointLabels: {
          font: { size: 14, family: "Quicksand", weight: "600" },
          color: rootSkillData.map(
            (_, i) => rootSkillColors[i % rootSkillColors.length].border,
          ),
        },
        grid: {
          color: "rgba(234, 236, 241, 0.8)",
        },
      },
    },
  };

  const overviewBarData = {
    labels: rootSkillData.map((d) => d.name),
    datasets: [
      {
        label: i18n.t("learner_score"),
        data: rootSkillData.map((d) => d.score),
        backgroundColor: rootSkillData.map(
          (_, i) => rootSkillColors[i % rootSkillColors.length].bg,
        ),
        borderColor: rootSkillData.map(
          (_, i) => rootSkillColors[i % rootSkillColors.length].border,
        ),
        borderWidth: 2,
        borderRadius: 6,
        borderSkipped: false,
      },
    ],
  };

  const overviewBarOptions = {
    responsive: true,
    maintainAspectRatio: false,
    indexAxis: "y",
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        backgroundColor: "rgba(17, 72, 89, 0.9)",
        titleFont: { size: 14, family: "Quicksand" },
        bodyFont: { size: 13, family: "Quicksand" },
        padding: 12,
        cornerRadius: 8,
        callbacks: {
          title: (items) => {
            const idx = items[0].dataIndex;
            return rootSkillData[idx].name;
          },
          label: (context) =>
            `${i18n.t("learner_score")}: ${context.parsed.x.toFixed(1)}%`,
        },
      },
    },
    scales: {
      x: {
        beginAtZero: true,
        max: 100,
        ticks: {
          callback: (value) => `${value}%`,
          font: { size: 12, family: "Quicksand" },
          color: "#799BA6",
          padding: 6,
        },
        grid: {
          color: "rgba(234, 236, 241, 0.8)",
          drawBorder: false,
        },
      },
      y: {
        ticks: {
          font: { size: 14, family: "Quicksand", weight: "bold" },
          color: rootSkillData.map(
            (_, i) => rootSkillColors[i % rootSkillColors.length].border,
          ),
          padding: 8,
        },
        grid: {
          display: false,
        },
      },
    },
  };

  // Direct children (level 2) of each root, with aggregated descendant scores
  const groupedChildSkillData = useMemo(() => {
    const groups = {};
    relevantRootCodes.forEach((rootCode) => {
      const children = skillVersionCodeMEBList.filter(
        (s) => s.parentCode === rootCode,
      );
      const childData = children
        .map((child) => {
          const descendantIds = skillVersionCodeMEBList
            .filter((s) => {
              if (s.id === child.id) return true;
              let current = s;
              while (current && current.parentCode) {
                if (current.parentCode === child.code) return true;
                const parent = skillVersionCodeMEBList.find(
                  (p) => p.code === current.parentCode,
                );
                if (!parent) break;
                current = parent;
              }
              return false;
            })
            .map((s) => s.id);

          const allScores = [];
          descendantIds.forEach((id) => {
            if (skillScoresMap[id]) {
              allScores.push(...skillScoresMap[id]);
            }
          });

          return {
            code: child.code,
            name: i18n.t(child.name),
            score: computeAverageScore(allScores),
            hasData: allScores.length > 0,
          };
        })
        .filter((d) => d.hasData)
        .sort((a, b) => a.code.localeCompare(b.code));

      if (childData.length > 0) {
        groups[rootCode] = childData;
      }
    });
    return groups;
  }, [skillScoresMap, relevantRootCodes]);

  // Group leaf skills by their root skill
  const groupedLeafSkillData = useMemo(() => {
    const groups = {};
    leafSkillData.forEach((d) => {
      if (!groups[d.rootCode]) {
        groups[d.rootCode] = [];
      }
      groups[d.rootCode].push(d);
    });
    return groups;
  }, [leafSkillData]);


  const buildBarData = (skills, rootCode) => {
    const color = getRootColor(rootCode);
    return {
      labels: skills.map((d) => {
        return d.name.length > 50
          ? d.name.substring(0, 47) + "..."
          : d.name;
      }),
      datasets: [
        {
          label: i18n.t("learner_score"),
          data: skills.map((d) => d.score),
          backgroundColor: color.bg,
          borderColor: color.border,
          borderWidth: 2,
          borderRadius: 6,
          borderSkipped: false,
        },
      ],
    };
  };

  const buildBarOptions = (skills) => ({
    responsive: true,
    maintainAspectRatio: false,
    indexAxis: "y",
    layout: {
      padding: {
        left: 10,
      },
    },
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        backgroundColor: "rgba(17, 72, 89, 0.9)",
        titleFont: { size: 14, family: "Quicksand" },
        bodyFont: { size: 13, family: "Quicksand" },
        padding: 14,
        cornerRadius: 8,
        callbacks: {
          title: (items) => {
            const idx = items[0].dataIndex;
            return skills[idx].name;
          },
          label: (context) =>
            `${i18n.t("learner_score")}: ${context.parsed.x.toFixed(1)}%`,
        },
      },
    },
    scales: {
      x: {
        beginAtZero: true,
        max: 100,
        ticks: {
          callback: (value) => `${value}%`,
          font: { size: 12, family: "Quicksand" },
          color: "#799BA6",
          padding: 6,
        },
        grid: {
          color: "rgba(234, 236, 241, 0.8)",
          drawBorder: false,
        },
      },
      y: {
        afterFit: (axis) => {
          axis.width = Math.max(axis.width, 320);
        },
        ticks: {
          font: { size: 13, family: "Quicksand" },
          color: "#55777F",
          padding: 8,
        },
        grid: {
          display: false,
        },
      },
    },
  });

  const hasData = rootSkillData.length > 0;

  return (
    <div className="qbj-root">
      <style>{skillGraphStyles}</style>
      {hasBothSources && !isSourceSelectionControlled ? (
        <div className="qbj-toggle-row">
          <div
            className="qbj-toggle-item"
            onClick={() => {
              setShowBranchAssignment(!showBranchAssignment);
            }}
          >
            <CheckBoxGlyph checked={showBranchAssignment} />
            <p>{i18n.t("branch_assignment_performance")}</p>
          </div>
          <div
            className="qbj-toggle-item"
            onClick={() => {
              setShowPersonalizedAssignment(!showPersonalizedAssignment);
            }}
          >
            <CheckBoxGlyph checked={showPersonalizedAssignment} />
            <p>{i18n.t("personalized_assignment_performance")}</p>
          </div>
        </div>
      ) : null}
      {!hasData ? (
        <EmptyState compact icon="target" title={i18n.t("no_skill_data")} />
      ) : (
        <>
          <div className="qbj-section">
            <div className="qbj-title-wrapper">
              <p className="qbj-title">
                {i18n.t("skill_radar_chart")}
              </p>
            </div>
            <div className="qbj-chart-row">
              {rootSkillData.length >= 3 ? (
                <div className="qbj-radar-card">
                  <Radar data={radarData} options={radarOptions} />
                </div>
              ) : null}
              <div
                className={`${rootSkillData.length >= 3 ? "qbj-overview-card-wide" : "qbj-overview-card-full"} qbj-overview-card`}
              >
                <div
                  className="qbj-chart-fill"
                  style={{
                    height: Math.max(250, rootSkillData.length * 60),
                  }}
                >
                  <Bar
                    data={overviewBarData}
                    options={overviewBarOptions}
                  />
                </div>
              </div>
            </div>
          </div>

          {rootSkillData
            .filter(
              (root) =>
                groupedLeafSkillData[root.code]?.length > 0 ||
                groupedChildSkillData[root.code]?.length > 0,
            )
            .map((root) => {
              const childSkills = groupedChildSkillData[root.code] || [];
              const leafSkills = groupedLeafSkillData[root.code] || [];
              const showParentComparison = childSkills.length > 0;
              const childBarHeight = Math.max(200, childSkills.length * 40);
              const leafBarHeight = Math.max(200, leafSkills.length * 40);

              return (
                <div key={root.code} className="qbj-group">
                  <div className="qbj-title-wrapper">
                    <p className="qbj-title">{root.name}</p>
                  </div>
                  {showParentComparison && (
                    <div className="qbj-card-mb">
                      <div style={{ height: childBarHeight }}>
                        <Bar
                          data={buildBarData(childSkills, root.code)}
                          options={buildBarOptions(childSkills)}
                        />
                      </div>
                    </div>
                  )}
                  {leafSkills.length > 0 && (
                    <div className="qbj-card">
                      <div style={{ height: leafBarHeight }}>
                        <Bar
                          data={buildBarData(leafSkills, root.code)}
                          options={buildBarOptions(leafSkills)}
                        />
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
        </>
      )}
    </div>
  );
};

export default SkillGraph;
